import {
  projectReflection,
  verifyReflectionAnchor,
} from './reflectionKeepProjection';

import type { ArtifactTextSelection } from './useArtifactTextSelection';

// Selection is projected solely from original measured body roots. Native
// Selection.toString() across sibling controls/composers is never source text.
export const readReflectionKeepSelection = (
  selection: Selection | null,
  root: HTMLElement,
  markdown: string,
): ArtifactTextSelection | null => {
  if (!selection || selection.rangeCount !== 1 || selection.isCollapsed) {
    return null;
  }
  const range = selection.getRangeAt(0);
  const blocks = projectReflection(markdown);
  const elements = Array.from(
    root.querySelectorAll<HTMLElement>('[data-block-id]'),
  );
  const endpointRoot = (node: Node): HTMLElement | null => {
    const element = node instanceof Element ? node : node.parentElement;
    const textRoot = element?.closest<HTMLElement>('[data-block-id]');
    return textRoot &&
      root.contains(textRoot) &&
      !element?.closest('[data-reflection-control]')
      ? textRoot
      : null;
  };
  const first = endpointRoot(range.startContainer);
  const last = endpointRoot(range.endContainer);
  if (!first || !last) {
    return null;
  }
  const from = elements.indexOf(first);
  const to = elements.indexOf(last);
  if (from < 0 || to < from) {
    return null;
  }
  const offset = (element: HTMLElement, node: Node, index: number) => {
    const prefix = document.createRange();
    prefix.selectNodeContents(element);
    prefix.setEnd(node, index);
    return prefix.toString().length;
  };
  const pieces = elements.slice(from, to + 1).map((element, index) => {
    const block = blocks.find((item) => item.id === element.dataset.blockId);
    if (!block || element.textContent !== block.visibleText) {
      return null;
    }
    const start =
      index === 0
        ? offset(element, range.startContainer, range.startOffset)
        : 0;
    const end =
      index === to - from
        ? offset(element, range.endContainer, range.endOffset)
        : block.visibleText.length;
    return {
      block,
      element,
      start,
      end,
      text: block.visibleText.slice(start, end),
    };
  });
  if (pieces.some((piece) => !piece)) {
    return null;
  }
  const included = pieces.filter((piece) => piece !== null);
  while (included.length && !included[0]?.text.trim()) {
    included.shift();
  }
  while (included.length && !included.at(-1)?.text.trim()) {
    included.pop();
  }
  const startPiece = included[0];
  const endPiece = included.at(-1);
  if (!startPiece || !endPiece) {
    return null;
  }
  startPiece.start +=
    startPiece.text.length - startPiece.text.trimStart().length;
  startPiece.text = startPiece.text.trimStart();
  endPiece.end -= endPiece.text.length - endPiece.text.trimEnd().length;
  endPiece.text = endPiece.text.trimEnd();
  const text = included.map((piece) => piece.text).join('\n\n');
  const blockId =
    startPiece.block.id === endPiece.block.id
      ? startPiece.block.id
      : `${startPiece.block.id}:${endPiece.block.id}`;
  if (
    !verifyReflectionAnchor(blocks, {
      blockId,
      startIndex: startPiece.start,
      endIndex: endPiece.end,
      selectedText: text,
      scope: 'selection',
    })
  ) {
    return null;
  }
  return {
    absTop: startPiece.element.getBoundingClientRect().top + window.scrollY,
    blockId,
    startIndex: startPiece.start,
    endIndex: endPiece.end,
    text,
  };
};
